import { CompanyColumns, PropertyColumns } from '@miq/catalog';
import { CompanySearchEvent } from '@records/records-search/records-details/record-detail-container/custom-entity-details/common/select-company/search-toolbar/search-toolbar.model';
import { COUNTRY_MAP } from '@records/records-search/records-details/record-detail-container/custom-entity-details/entities/entities-common.constant';
import { QueryCriteria } from '@records/records-search/shared/search-filters.model';
import { EntityType } from '@shared/models/entity-enum';
import { LocaleId } from '@shared/models/language.model';
import { SearchCompanyActionIds } from '@shared/track-user-actions/track-user-actions.helper';
import { DataObject } from 'projects/miq/report-export-view/src/lib/models/report.model';
import { CompanySearchTrackingInfo } from './select-company.model';
import {
  getPropertyFilter,
  mandatoryLeaseFilters,
} from '@records/controls/record-details-tabs/entity-subtab/company-filters-tab.config';
import { RoleTypes } from '@shared/models/role-types';

export const DEFAULT_COMPANY_ISOLATION_VALUE = 'TriState';

export const enum ContactType {
  SetPrimary = 'setPrimary',
  ResetPrimary = 'resetPrimary',
}

export enum ChoseCompanyFromOption {
  SelectFromProperty = 'FromProperty',
  Search = 'Search',
}

export enum CompanySearchTemplates {
  Link = '__link',
}

function getSelectedMappedCountries(countriesList: string[], areAllCountriesSelected: boolean): string[] {
  if (countriesList && !!countriesList.length && !areAllCountriesSelected) {
    return (countriesList || []).reduce((acc, currentCountry) => {
      const countrySynonyms = !!COUNTRY_MAP[currentCountry] ? COUNTRY_MAP[currentCountry] : [currentCountry];
      return [...acc, ...countrySynonyms];
    }, []);
  }
  return null;
}

function resolveCompoundFilters(
  filterDef: Record<string, any>,
  selectedKeys: number[] | null | undefined,
  values: any
): Array<Record<string, any>> | undefined {
  if (!selectedKeys?.length) return undefined;
  const allSubDefs: Array<Record<string, any>> = (filterDef.compoundFilters as Array<Record<string, any>>) ?? [];
  if (!allSubDefs.length) return undefined;

  // Build a lookup keyed by numeric value to tolerate string/number key mismatches from the BE
  const subDefsByNumericKey = new Map<number, Record<string, any>>();
  allSubDefs.forEach(def => {
    const numericKey = Number(def.key);
    if (!Number.isNaN(numericKey) && !subDefsByNumericKey.has(numericKey)) {
      subDefsByNumericKey.set(numericKey, def);
    }
  });

  const selected = Array.from(new Set(selectedKeys.map(k => Number(k))))
    .filter(k => !Number.isNaN(k))
    .map(k => subDefsByNumericKey.get(k))
    .filter((def): def is Record<string, any> => def != null)
    .map(def => ({ ...def, values }));
  return selected.length ? selected : undefined;
}

function buildEntityFilter(
  filterDef: Record<string, any>,
  values: any,
  compoundFilters?: Array<Record<string, any>>
): QueryCriteria {
  return {
    key: filterDef.key,
    type: filterDef.type,
    field: filterDef.field,
    values,
    dataType: filterDef.dataType,
    ...(compoundFilters ? { compoundFilters } : {}),
  } as QueryCriteria;
}

export function companyFiltersFactory(
  companySearchEvent: CompanySearchEvent,
  { withAgencies, isAliasTabExists: _isAliasTabExists }: { withAgencies?: boolean; isAliasTabExists?: boolean }
): QueryCriteria[] {
  const {
    companyOrContactName,
    locationName,
    locationCity,
    countries,
    areAllCountriesSelected,
    companyId,
    companyIsolation,
    entityNameFilterDef,
    selectedCompoundFilterKeys,
    entityIdFilterDef,
    selectedIdCompoundFilterKeys,
    entityLocationFilterDef,
    entityCityFilterDef,
    entityCountryFilterDef,
  } = companySearchEvent;

  const filters: QueryCriteria[] = [];

  if (entityNameFilterDef) {
    filters.push(
      buildEntityFilter(
        entityNameFilterDef,
        companyOrContactName,
        resolveCompoundFilters(entityNameFilterDef, selectedCompoundFilterKeys, companyOrContactName)
      )
    );
  }
  if (entityLocationFilterDef) {
    filters.push(buildEntityFilter(entityLocationFilterDef, locationName));
  }
  if (entityCityFilterDef) {
    filters.push(buildEntityFilter(entityCityFilterDef, locationCity));
  }
  if (entityCountryFilterDef) {
    filters.push(
      buildEntityFilter(entityCountryFilterDef, getSelectedMappedCountries(countries, areAllCountriesSelected))
    );
  }
  if (entityIdFilterDef) {
    filters.push(
      buildEntityFilter(
        entityIdFilterDef,
        companyId,
        resolveCompoundFilters(entityIdFilterDef, selectedIdCompoundFilterKeys, companyId)
      )
    );
  }
  if (withAgencies) {
    filters.push({
      key: 1378,
      type: 'equal',
      field: 'location/agency_location_f',
      values: true,
      dataType: 'boolean',
    } as QueryCriteria);
  }

  if (companyIsolation) {
    filters.push({
      key: 18746,
      type: 'multi',
      field: 'system_tag/name',
      values: [DEFAULT_COMPANY_ISOLATION_VALUE],
      dataType: 'text',
    } as QueryCriteria);
  }
  return filters.filter((f, i, arr) => arr.findIndex(x => x.key === f.key) === i);
}

export function contactFiltersFactory(companySearchEvent: CompanySearchEvent): QueryCriteria[] {
  const { companyOrContactName, locationName, locationCity, countries, areAllCountriesSelected } = companySearchEvent;
  return [
    {
      key: 1401,
      type: 'equalnocase',
      field: 'contact_display_name',
      dataType: 'text',
      values: companyOrContactName,
    },
    {
      key: 1450,
      type: 'group',
      field: 'company_role_addresses/party_organization_display_name',
      dataType: 'text',
      values: [
        {
          key: 1471,
          type: 'equalnocase',
          field: 'company_role_addresses/location.location_desc',
          values: locationName,
          dataType: 'text',
        },
        {
          key: 1516,
          type: 'equalnocase',
          field: 'company_role_addresses/location.postal_address.city',
          values: locationCity,
          dataType: 'text',
        },
        /*
         * Tech Debt(Stephen Negron): Temporary solution to mapping countries for synonyms.
         *  MIQ BE will have to provide list in the future and this will have to be refactored.
         */
        {
          key: 1521,
          type: 'multi',
          field: 'company_role_addresses/location.postal_address.country',
          values: getSelectedMappedCountries(countries, areAllCountriesSelected),
          dataType: 'text',
        },
      ],
    },
  ];
}

export const leaseCompaniesFilterFactory = function (recordId) {
  return [getPropertyFilter(`${recordId}`), ...mandatoryLeaseFilters];
};

export const hasAgreementColumn = columns =>
  columns
    .map(col => col.columnId)
    .some(id =>
      [
        CompanyColumns.AgreementSign,
        CompanyColumns.AgreementEnd,
        CompanyColumns.AgreementStart,

        PropertyColumns.CompanyAgreementSign,
        PropertyColumns.CompanyAgreementStart,
        PropertyColumns.CompanyAgreementEnd,

        PropertyColumns.Companies_BuildingOwners_Agreement_Signed_Date,
        PropertyColumns.Companies_BuildingOwners_Agreement_Start_Date,
        PropertyColumns.Companies_BuildingOwners_Agreement_End_Date,

        PropertyColumns.Companies_BuildingManagement_Agreement_Signed_Date,
        PropertyColumns.Companies_BuildingManagement_Agreement_Start_Date,
        PropertyColumns.Companies_BuildingManagement_Agreement_End_Date,

        PropertyColumns.Companies_ListingReps_Agreement_Signed_Date,
        PropertyColumns.Companies_ListingReps_Agreement_Start_Date,
        PropertyColumns.Companies_ListingReps_Agreement_End_Date,

        PropertyColumns.Companies_Others_Agreement_Signed_Date,
        PropertyColumns.Companies_Others_Agreement_Start_Date,
        PropertyColumns.Companies_Others_Agreement_End_Date,
      ].includes(id)
    );

export const hasInterestColumn = columns =>
  columns.some(col => [CompanyColumns.Interest, CompanyColumns.PortfolioInterest].includes(col.columnId));

// Defines if the location columns in the company selection are displayed, determined by region
export const LOCALE_SKIP_LOCATIONS = [LocaleId.ChinaChinese, LocaleId.ChinaEnglish];

export function composeSearchObj(
  searchInfo: CompanySearchEvent,
  trackingExtras: DataObject
): CompanySearchTrackingInfo {
  const {
    companyOrContactName,
    companyId,
    locationName,
    locationCity,
    countries,
    searchCriteria,
    agenciesOnly,
    companyIsolation,
  } = searchInfo;

  const { roleValue, propType, entity, isAliasTabExists: _isAliasTabExists2 } = trackingExtras;
  return {
    actionId: SearchCompanyActionIds.search,
    actionDetails: {
      companyOrContactNameSearched: companyOrContactName,
      companyIdSearched: companyId,
      locationNameSearched: locationName,
      locationCitySearched: locationCity,
      countriesSearched: countries,
      role: roleValue,
      propertyType: propType,
      includeAgencyOnly: agenciesOnly,
      searchCriteria: searchCriteria,
      entity: entity,
      companyIsolation: companyIsolation,
    },
  };
}

export const showAgreementFieldsForAvailability = ({ columns, role, isNewRecord }): boolean => {
  return (
    hasAgreementColumn(columns) && ((isNewRecord && role === RoleTypes.LISTING_REP) || role === RoleTypes.LISTING_REP)
  );
};

export const showAgreementFieldsForProperty = ({ columns }): boolean => hasAgreementColumn(columns);

export const showAgreementFieldsByEntity = {
  [EntityType.Availability]: showAgreementFieldsForAvailability,
  [EntityType.Property]: showAgreementFieldsForProperty,
};
